import React from "react";
import { Navigate } from "react-router";

import AuthLogs from "../../../views/pages/dashboard/admin/AuthLogs";
import Charges from "../../../views/pages/dashboard/admin/Charges";
import Connectors from "../../../views/pages/dashboard/admin/Connectors";
import CryptoPaymentSettings from "../../../views/pages/dashboard/admin/CryptoPaymentSettings";
import EmailLogs from "../../../views/pages/dashboard/admin/EmailLogs";
import EmailTemplates from "../../../views/pages/dashboard/admin/EmailTemplates";
import EmailTesting from "../../../views/pages/dashboard/admin/EmailTesting";
import InvoiceTesting from "../../../views/pages/dashboard/admin/InvoiceTesting";
import ManualPayments from "../../../views/pages/dashboard/admin/ManualPayments";
import MissingPayments from "../../../views/pages/dashboard/admin/MissingPayments";
import Overview from "../../../views/pages/dashboard/admin/Overview";
import Refunds from "../../../views/pages/dashboard/admin/Refunds";
import Settings from "../../../views/pages/dashboard/admin/Settings";
import TaxReports from "../../../views/pages/dashboard/admin/TaxReports";
import UpcomingCharges from "../../../views/pages/dashboard/admin/UpcomingCharges";
import User from "../../../views/pages/dashboard/admin/User";
import ExchangeAccount from "../../../views/pages/dashboard/shared/ExchangeAccount";
import type { RouteProps } from "../../routeTypes";
// Sub-routes
import adminStatsSubRoutes from "./adminStats";

export default (props: RouteProps) => {
  const { t } = props;
  return [
    // ==========================================
    // OVERVIEW (admin landing page)
    // ==========================================
    {
      name: t("routes.admin.overview", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "overview",
      showOnNav: true,
      icon: "fas fa-th-large text-default",
      component: <Overview />,
    },
    // Hidden routes (no nav)
    {
      name: t("routes.admin.stats", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "stats",
      subRoutes: adminStatsSubRoutes(props),
    },
    {
      name: t("routes.admin.users", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "users",
      icon: "fas fa-users text-default",
      subRoutes: [
        {
          name: t("routes.admin.user", { ns: "dashboard" }),
          path: ":userId",
          showOnNav: false,
          icon: "fas fa-user text-default",
          subRoutes: [
            {
              name: t("routes.admin.user", { ns: "dashboard" }),
              path: "",
              showOnNav: false,
              icon: "fas fa-user text-default",
              component: <User />,
            },
            {
              name: t("routes.admin.bot", { ns: "dashboard" }),
              path: "exchange-account/:exchangeAccountId",
              showOnNav: false,
              icon: "fas fa-robot text-default",
              component: <ExchangeAccount />,
            },
          ],
        },
      ],
    },

    // ==========================================
    // CONNECTORS (native MT5 connector admin)
    // ==========================================
    {
      name: t("routes.admin.connectors", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "connectors",
      showOnNav: true,
      icon: "fas fa-plug text-default",
      component: <Connectors />,
    },

    // ==========================================
    // BILLING & PAYMENTS
    // ==========================================
    {
      name: t("routes.admin.billing", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "billing",
      showOnNav: true,
      icon: "fas fa-file-invoice-dollar text-default",
      subRoutes: [
        {
          name: t("routes.admin.charges", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "charges",
          showOnNav: true,
          component: <Charges />,
        },
        {
          name: t("routes.admin.upcoming", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "upcoming",
          showOnNav: true,
          component: <UpcomingCharges />,
        },
        {
          name: t("routes.admin.missingPayments", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "missing-payments",
          showOnNav: true,
          component: <MissingPayments />,
        },
        {
          name: t("routes.admin.manualPayments", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "manual-payments",
          showOnNav: true,
          component: <ManualPayments />,
        },
        {
          name: t("routes.admin.refunds", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "refunds",
          showOnNav: true,
          component: <Refunds />,
        },
      ],
    },
    {
      path: "payouts",
      accessRoles: ["Admin"],
      component: <Navigate to="/stats/affiliates/payouts" replace={true} />,
    },

    // ==========================================
    // TAX & INVOICING
    // ==========================================
    {
      name: t("routes.admin.taxInvoicing", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "tax",
      showOnNav: true,
      icon: "fas fa-file-invoice text-default",
      subRoutes: [
        {
          name: t("routes.admin.taxReports", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "reports",
          showOnNav: true,
          component: <TaxReports />,
        },
      ],
    },

    // ==========================================
    // COMMUNICATIONS
    // ==========================================
    {
      name: t("routes.admin.email", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "email",
      showOnNav: true,
      icon: "fas fa-envelope text-default",
      subRoutes: [
        {
          name: t("routes.admin.emailTemplates", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "templates",
          showOnNav: true,
          component: <EmailTemplates />,
        },
        {
          name: t("routes.admin.emailLogs", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "logs",
          showOnNav: true,
          component: <EmailLogs />,
        },
      ],
    },

    // ==========================================
    // SECURITY
    // ==========================================
    {
      name: t("routes.admin.security", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "security",
      showOnNav: true,
      icon: "fas fa-shield-alt text-default",
      subRoutes: [
        {
          name: t("routes.admin.authLogs", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "auth-logs",
          showOnNav: true,
          component: <AuthLogs />,
        },
      ],
    },

    // ==========================================
    // SETTINGS
    // ==========================================
    {
      name: t("routes.admin.settings", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "settings",
      showOnNav: true,
      icon: "fas fa-cog text-default",
      subRoutes: [
        {
          name: t("routes.admin.general", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "general",
          showOnNav: true,
          component: <Settings />,
        },
        {
          name: t("routes.admin.cryptoPayments", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "crypto-payments",
          showOnNav: true,
          component: <CryptoPaymentSettings />,
        },
      ],
    },

    // ==========================================
    // DEV TOOLS (internal QA tooling)
    // ==========================================
    {
      name: t("routes.admin.devTools", { ns: "dashboard" }),
      accessRoles: ["Admin"],
      path: "dev",
      showOnNav: true,
      icon: "fas fa-flask text-muted",
      subRoutes: [
        {
          name: t("routes.admin.invoiceTesting", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "invoice-testing",
          showOnNav: true,
          component: <InvoiceTesting />,
        },
        {
          name: t("routes.admin.emailTesting", { ns: "dashboard" }),
          accessRoles: ["Admin"],
          path: "email-testing",
          showOnNav: true,
          component: <EmailTesting />,
        },
      ],
    },

    // ==========================================
    // REDIRECTS
    // ==========================================
    {
      path: "",
      accessRoles: ["Admin"],
      index: true,
      component: <Navigate to="/overview" replace={true} />,
    },
    {
      path: "*",
      accessRoles: ["Admin"],
      index: true,
      component: <Navigate to="/stats/users/active-clients" replace={true} />,
    },
  ];
};
